import type { IBound, IVec } from './types';

export type ViewportState = {
  /** 视口左上角对应的模型坐标 */
  viewportX: number;
  viewportY: number;
  zoom: number;
};

export const ZOOM_MIN = 0.1;
export const ZOOM_MAX = 6.0;
export const ZOOM_STEP = 0.2;
export const FIT_TO_SCREEN_PADDING = 100;

/**
 * 视口：缩放、平移与坐标换算的唯一真源。
 * 借鉴 AFFiNE `Viewport` 的设计（单一坐标真源、缩放钳制、事件驱动），
 * 这里去掉了其移动端限流分支，保留桌面语义。
 */
export class Viewport {
  /** 画布容器在页面中的位置（client 坐标偏移）。 */
  left = 0;
  top = 0;
  /** 容器尺寸（CSS 像素）。 */
  width = 0;
  height = 0;

  viewportX = 0;
  viewportY = 0;
  zoom = 1;

  minZoom = ZOOM_MIN;
  maxZoom = ZOOM_MAX;

  private listeners = new Set<(state: ViewportState) => void>();

  onChange(fn: (state: ViewportState) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    const state = this.state;
    for (const fn of this.listeners) fn(state);
  }

  /** 对外提交一次变更通知（供动画/巡航等外部驱动使用）。 */
  notify(): void {
    this.emit();
  }

  get state(): ViewportState {
    return { viewportX: this.viewportX, viewportY: this.viewportY, zoom: this.zoom };
  }

  setContainer(left: number, top: number, width: number, height: number): void {
    this.left = left;
    this.top = top;
    this.width = width;
    this.height = height;
    this.emit();
  }

  /** client 坐标（页面）→ 模型坐标 */
  clientToModel(coord: IVec): IVec {
    return [
      this.viewportX + (coord[0] - this.left) / this.zoom,
      this.viewportY + (coord[1] - this.top) / this.zoom,
    ];
  }

  /** 容器内 screen 坐标 → 模型坐标 */
  screenToModel(coord: IVec): IVec {
    return [this.viewportX + coord[0] / this.zoom, this.viewportY + coord[1] / this.zoom];
  }

  /** 模型坐标 → 容器内 screen 坐标 */
  modelToScreen(point: IVec): IVec {
    return [(point[0] - this.viewportX) * this.zoom, (point[1] - this.viewportY) * this.zoom];
  }

  /** 模型长度 → screen 长度 */
  toScreenLength(len: number): number {
    return len * this.zoom;
  }

  /** screen 长度 → 模型长度 */
  toModelLength(len: number): number {
    return len / this.zoom;
  }

  /** 屏幕像素平移。 */
  panBy(dx: number, dy: number): void {
    this.viewportX -= dx / this.zoom;
    this.viewportY -= dy / this.zoom;
    this.emit();
  }

  /** 平移使得给定的模型点位于屏幕点。 */
  panToModelPoint(model: IVec, screen: IVec): void {
    this.viewportX = model[0] - screen[0] / this.zoom;
    this.viewportY = model[1] - screen[1] / this.zoom;
    this.emit();
  }

  setCenter(model: IVec): void {
    this.viewportX = model[0] - this.width / 2 / this.zoom;
    this.viewportY = model[1] - this.height / 2 / this.zoom;
    this.emit();
  }

  get center(): IVec {
    return [
      this.viewportX + this.width / 2 / this.zoom,
      this.viewportY + this.height / 2 / this.zoom,
    ];
  }

  private clampZoom(zoom: number): number {
    return Math.min(this.maxZoom, Math.max(this.minZoom, zoom));
  }

  /**
   * 缩放到指定倍率，并保持屏幕锚点处的模型坐标不动。
   * @param anchor 容器内 screen 坐标，默认容器中心
   */
  zoomTo(zoom: number, anchor?: IVec): void {
    const next = this.clampZoom(zoom);
    if (next === this.zoom) return;
    const anchorScreen: IVec = anchor ?? [this.width / 2, this.height / 2];
    const before = this.screenToModel(anchorScreen);
    this.zoom = next;
    this.panToModelPoint(before, anchorScreen);
  }

  zoomIn(anchor?: IVec): void {
    this.zoomTo(this.zoom * (1 + ZOOM_STEP), anchor);
  }

  zoomOut(anchor?: IVec): void {
    this.zoomTo(this.zoom / (1 + ZOOM_STEP), anchor);
  }

  /** 视口覆盖的模型包围盒（可见区域）。 */
  get viewBounds(): IBound {
    return {
      x: this.viewportX,
      y: this.viewportY,
      w: this.width / this.zoom,
      h: this.height / this.zoom,
    };
  }

  /** 扩大后的渲染包围盒（overscan，用于预渲染/懒挂载）。 */
  overscanBounds(ratio = 0.25): IBound {
    const b = this.viewBounds;
    const dx = b.w * ratio;
    const dy = b.h * ratio;
    return { x: b.x - dx, y: b.y - dy, w: b.w + dx * 2, h: b.h + dy * 2 };
  }

  /** 适配到给定包围盒。 */
  fitToScreen(bound: IBound, padding = FIT_TO_SCREEN_PADDING): void {
    if (this.width === 0 || this.height === 0) return;
    const b = bound.w === 0 && bound.h === 0
      ? { ...bound, w: 1, h: 1 }
      : bound;
    const availableW = Math.max(1, this.width - padding * 2);
    const availableH = Math.max(1, this.height - padding * 2);
    const zoom = this.clampZoom(
      Math.min(availableW / Math.max(1, b.w), availableH / Math.max(1, b.h))
    );
    this.zoom = zoom;
    const cx = b.x + b.w / 2;
    const cy = b.y + b.h / 2;
    this.viewportX = cx - this.width / 2 / zoom;
    this.viewportY = cy - this.height / 2 / zoom;
    this.emit();
  }

  /** 适配一个点集合。 */
  fitToPoints(points: IVec[], padding = FIT_TO_SCREEN_PADDING): void {
    if (points.length === 0) {
      this.setCenter([0, 0]);
      return;
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of points) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    this.fitToScreen({ x: minX, y: minY, w: maxX - minX, h: maxY - minY }, padding);
  }
}